import React from 'react'
import { Link } from '@tanstack/react-router'
import { BusswerksLogo } from '@/components/BusswerksLogo'
import { UserMenu } from '@/components/UserMenu'
import { TransportChip } from '@/components/TransportChip'
import {
  WorkspaceSwitcher,
  type WorkspaceKey,
} from '@/components/WorkspaceSwitcher'

interface WorkspaceHeaderProps {
  /**
   * Centered single-line title — page name for the static workspaces
   * (MIDI Router, Slicer) or the active project's name for the
   * sequencer. Mirrors the layout-editor header where the layout
   * name sits in the same slot.
   */
  title: React.ReactNode
  /**
   * Small status text rendered to the left of `UserMenu`. Used by the
   * sequencer for "Auto-saved / Saving / 🔒 Save" parity with the
   * editor's "Auto-save ready / Saving / Saved" indicator. Pass a
   * string for the simple case or a node for richer treatments
   * (badges, multi-state buttons).
   */
  status?: React.ReactNode
  /**
   * Extra right-side actions placed between `status` and `UserMenu`.
   * Kept narrow on purpose so the header stays single-row at iPad
   * widths.
   */
  rightExtras?: React.ReactNode
  /**
   * Extra left-side content placed between the Back link and the
   * centered title. Used by pages that live inside a `RoomProvider`
   * (Sequencer, future editor uses) to render `<MaybePresenceDock />`
   * — which calls `useOthers` / `useMyPresence` internally and would
   * throw if rendered on a room-less page like `/midi` or `/slicer`.
   * So presence is opt-in per page, not baked into the shared header.
   */
  leftExtras?: React.ReactNode
  /**
   * Which workspace pill to render highlighted in the
   * MIDI · Sequencer · Slicer quick-pick switcher. Lives just
   * before `UserMenu` so it sits in the same spot on every
   * workspace page — lets users jump between the three surfaces
   * without having to go through Home first. Omit the prop to
   * hide the switcher (e.g. on pages that aren't one of the three
   * workspaces).
   */
  workspaceSwitcher?: WorkspaceKey
}

/**
 * Compact workspace header — same h-10 / safe-area-padded shape as
 * the layout editor's nav bar. Used by every workspace route (MIDI,
 * Slicer, Sequencer) so navigating between them feels consistent.
 *
 * Why not extend `AppHeader`: AppHeader is the standard product
 * header (logo + subtitle + flex-wrap right-side actions) used for
 * Home / Dashboard / marketing pages. The editor has historically
 * carried its own minimal nav for the canvas-first layout, and the
 * other workspace pages now follow that pattern so the four
 * surfaces share a single visual idiom.
 */
export const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  title,
  status,
  rightExtras,
  leftExtras,
  workspaceSwitcher,
}) => {
  return (
    <>
      {/* Top safe-area spacer matches the editor — pushes the header
          below the iOS status bar / Dynamic Island. Collapses to
          zero on web. */}
      <div className="pt-safe bg-surface-overlay" />
      <header className="h-10 bg-surface-overlay border-b border-brd-subtle flex items-center justify-between px-4 py-1.5">
        <div className="flex items-center gap-4">
          <BusswerksLogo size="sm" className="text-content-primary" />
          <Link
            to="/home"
            className="flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary transition-colors duration-150 px-2 py-1 rounded hover:bg-accent-muted"
            title="Back to home"
          >
            <span>←</span>
            <span>Back</span>
          </Link>
          {leftExtras}
        </div>
        <div className="text-xs text-content-secondary truncate max-w-[50%] flex items-center gap-1.5">
          <span className="truncate">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {rightExtras}
          {status !== undefined && status !== null && (
            <div className="text-xs text-content-tertiary">{status}</div>
          )}
          {/* Transport chip appears on every workspace page so the user
              can trigger / stop playback without navigating back to
              the editor. Includes /layouts now — users asked for the
              controls to persist across every surface. */}
          <TransportChip inEditor={workspaceSwitcher === 'werkshop'} />
          {workspaceSwitcher && (
            <WorkspaceSwitcher active={workspaceSwitcher} />
          )}
          <UserMenu />
        </div>
      </header>
    </>
  )
}
